import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { noticeActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { FileSelectDialog, InformModal } from '../../common/SharedComponents';
import { NoticesApi, ListApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '../../common/SharedEvents';
import { LoadPanel } from 'devextreme-react/load-panel';
import DateBox from 'devextreme-react/date-box';
import { NoticeType, NoticeTypeOptions } from '@apply/constants';
import { IncompleteReasonType } from '../../loans/common/LoanConstants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { DeclineReasonType, DeclineReasonTypeOptions } from '../../applications/common/ApplicationConstants';

const AddNoticeModal = () => {
    const { isOpen, parentTypeId, parentId, noticeTypeId } = useSelector((state) => state.notices.addNoticeModal);
    const { dispatch } = store;

    const [notice, set_notice] = useState(null);
    const [incompleteReasons, set_incompleteReasons] = useState([]);
    const [noticeDate, set_noticeDate] = useState(new Date());
    const [noticeFile, set_noticeFile] = useState(null);
    const [sentByUserId, set_sentByUserId] = useState('');
    const [selectedRecipientUserId, set_selectedRecipientUserId] = useState('');
    const [incompleteReasonTypeIds, set_incompleteReasonTypeIds] = useState([]);
    const [companyTaxReturnAdditionalInfo, set_companyTaxReturnAdditionalInfo] = useState('');
    const [personalTaxReturnAdditionalInfo, set_personalTaxReturnAdditionalInfo] = useState('');
    const [declineReasonTypeIds, set_declineReasonTypeIds] = useState([]);
    const [declineReason, set_declineReason] = useState('');
    const [unableToVerifyIdentityDeclineReason, set_unableToVerifyIdentityDeclineReason] = useState('');
    const [reasonsLocked, set_reasonsLocked] = useState(false);
    const [employees, set_employees] = useState([]);
    const [potentialRecipients, set_potentialRecipients] = useState([]);

    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const [informModalTitle, set_informModalTitle] = useState('');
    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalText, set_informModalText] = useState('');

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { paddingLeft: '5px', paddingTop: '10px' },
        formRow: { marginBottom: '0' },
    };

    const noticesApi = new NoticesApi.NoticesApi(applicationInsights);
    const listApi = new ListApi.ListApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);

    const refreshEvents = new RefreshEvents();

    const stringSorter = (a, b) => {
        const nameA = a.toUpperCase(); // ignore upper and lowercase
        const nameB = b.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        // names must be equal
        return 0;
    };

    useEffect(() => {
        const loadListsAsync = async () => {
            await _loadBfiEmployees();
        };
        loadListsAsync();
    }, []);

    useEffect(() => {
        async function parentIdChanged() {
            resetState();

            if (!isOpen || !parentId) {
                return;
            }

            await loadNoticeInfo(parentTypeId, parentId, noticeTypeId);
        }

        parentIdChanged();
    }, [isOpen, parentTypeId, parentId, noticeTypeId]);

    const _loadPotentialRecipients = async (propertyId) => {
        let data = [];

        switch (notice.noticeTypeId) {
            case NoticeType.PostClosingCertNotice:
                data = await noticesApi.getPostClosingCertRecipientListByProperty(propertyId);
                break;
        }

        set_potentialRecipients(data.result);
    };

    useEffect(() => {
        async function noticeChanged() {
            if (!notice) {
                return;
            }
            if (notice.selectRecipient == true) {
                await _loadPotentialRecipients(notice.parentId);
            }

            if (notice.noticeTypeId === NoticeType.IncompleteFile) {
                await loadIncompleteReasons(notice);
                if (notice.incompleteReasonTypeIds) {
                    set_reasonsLocked(true);
                    set_incompleteReasonTypeIds(notice.incompleteReasonTypeIds);
                }
            } else if (notice.noticeTypeId === NoticeType.AdverseAction) {
                if (notice.declineReasonTypeIds) {
                    set_reasonsLocked(true);
                    set_declineReasonTypeIds(notice.declineReasonTypeIds);
                    set_declineReason(notice.declineReasonOther ?? '');
                    set_unableToVerifyIdentityDeclineReason(notice.declineReasonUnableToVerifyIdentity ?? '');
                }
            }

            set_loadingOpen(false);
        }

        noticeChanged();
    }, [notice]);

    const _handleClose = () =>
        dispatch(
            noticeActions.setAddNoticeModal({
                addNoticeModal: { isOpen: false },
            })
        );

    const _loadBfiEmployees = async () => {
        const data = await listApi.getBfiEmployees();
        set_employees(data);
    };

    const loadNoticeInfo = async (parentTypeId, parentId, noticeTypeId) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const model = {
                noticeTypeId,
                parentTypeId,
                parentId,
            };
            const response = await noticesApi.getAddNotice(model);
            if (response.statusCode !== 200) {
                return;
            }
            const ln = response.result;
            set_notice(ln);
        } finally {
            set_loadingOpen(false);
        }
    };

    const resetState = () => {
        set_notice(null);
        set_noticeFile(null);
        set_sentByUserId('');
        set_incompleteReasonTypeIds([]);
        set_companyTaxReturnAdditionalInfo('');
        set_personalTaxReturnAdditionalInfo('');
        set_declineReasonTypeIds([]);
        set_declineReason('');
        set_unableToVerifyIdentityDeclineReason('');
        set_selectedRecipientUserId('');
        set_reasonsLocked(false);
        set_noticeDate(new Date());
        set_loadingOpen(false);
    };

    const loadIncompleteReasons = async (notice) => {
        if (!notice || !notice.id) {
            return;
        }
        set_loadingMessage('Loading...');
        set_loadingOpen(true);

        const response = await listApi.getIncompleteReasonsForNotice(notice.id);
        set_incompleteReasons(response.result);
    };

    const canSubmit = () => {
        let isOk =
            noticeDate &&
            sentByUserId &&
            noticeFile &&
            (!notice.selectRecipient || (notice.selectRecipient && selectedRecipientUserId));
        return isOk;
    };

    const emailNotice = async (noticeId) => {
        try {
            set_loadingMessage(`Requesting Notice Emailing...`);
            set_loadingOpen(true);

            const response = await noticesApi.emailNotice(noticeId);
            if (response.statusCode !== 200) {
                throw new Error('Email request failed unexpectedly!');
            }
            return true;
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const submitFileAndEmail = async (e, email) => {
        e.preventDefault();

        try {
            const model = {
                parentTypeId: notice.parentTypeId,
                parentId: notice.parentId,
                noticeTypeId: notice.noticeTypeId,
                sentByUserId,
                noticeDate,
                selectRecipient: notice.selectRecipient,
                selectedRecipientUserId: notice.selectRecipient ? selectedRecipientUserId : undefined,
            };

            set_loadingMessage(`Uploading new files...`);
            set_loadingOpen(true);

            const upLoadResult = await fileApi.uploadTempFiles([noticeFile]);
            let result = upLoadResult.result;
            if (result && result.length > 0) {
                model.tempFileName = result[0].tempName;
            }

            set_loadingMessage('Creating notice...');

            const response = await noticesApi.setAddNotice(model);
            if (response.statusCode !== 200) {
                return;
            }
            const noticeId = response.result;

            handleAdminSuccess('Notice saved successfully.');

            if (notice.canEmail && email) {
                var ok = await emailNotice(noticeId);
                if (!ok) {
                    return;
                }
            }
            refreshEvents.dispatchRefreshEvent();

            dispatch(
                noticeActions.setViewNoticeModal({
                    viewNoticeModal: { isOpen: true, noticeId },
                })
            );

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const setIncompleteReasonTypeId = (checked, id) => {
        const index = incompleteReasonTypeIds.indexOf(id);
        if (checked) {
            if (index < 0) {
                set_incompleteReasonTypeIds([...incompleteReasonTypeIds, id]);
            }
        } else {
            if (index > -1) {
                set_incompleteReasonTypeIds(incompleteReasonTypeIds.filter((i) => i !== id));
            }
        }
    };

    const onFileSelectSubmit = (id, files) => {
        if (!files || files.length === 0) {
            return;
        }

        set_noticeFile(files[0]);
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const openFileSelect = () => {
        const options = {
            ownerId: null,
            isOpen: true,
            maxFiles: 1,
            fileTypeName: 'Notice File',
        };
        set_fileSelectOptions(options);
    };

    const renderPropertyItem = (address) => {
        return (
            <div>
                {address && (
                    <div
                        style={{
                            marginTop: '5px',
                            marginBottom: '10px',
                            fontWeight: 'bold',
                        }}
                    >
                        {address.street1} {address.street2} {address.city}, {address.state} {address.zipCode}
                    </div>
                )}
            </div>
        );
    };

    const getModalTitle = () => {
        return 'Manual ' + NoticeTypeOptions.find((f) => f.id === notice.noticeTypeId).name;
    };

    const setDeclineReasonTypeId = (checked, id) => {
        const index = declineReasonTypeIds.indexOf(id);
        if (checked) {
            if (index < 0) {
                set_declineReasonTypeIds([...declineReasonTypeIds, id]);
            }
        } else if (index > -1) {
            set_declineReasonTypeIds(declineReasonTypeIds.filter((i) => i !== id));
        }
    };

    const RenderTaxReturnHelp = () => {
        return (
            <FontAwesomeIcon
                icon={faQuestionCircle}
                style={{
                    marginLeft: '5px',
                    color: '#2196f3',
                    cursor: 'pointer',
                }}
                onClick={openTaxReturnHelp}
            />
        );
    };

    const openTaxReturnHelp = () => {
        set_informModalTitle('Tax Filing');
        set_informModalText(
            "State what is missing in YYYY format along with 'return' and/or 'extension'.<br><br><b>REQUIREMENTS:</b> Most recent year AND if under extension, prior year also. Cutoff dates are April 1 and October 1 based on date application received. E.g. Application received date of April 1 – September 30, 2025 require 2024 return or 2024 extension + 2023 return. If October 1 – March 31, require 2024 return.<br><br><b>REQUIREMENTS, SBA:</b> Typically require 3 years of tax returns.</p>"
        );
        set_informModalOpen(true);
    };

    if (!isOpen || !notice) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '700px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form name='loanLostForm'>
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>{getModalTitle()}</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        Status: <b>{notice.status}</b>
                                                    </div>
                                                    {notice.selectRecipient && (
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            <label htmlFor='responsibleId'>
                                                                Notice Sent To: <span className='req'>*</span>
                                                            </label>
                                                            <select
                                                                id='selectedRecipientUserId'
                                                                value={selectedRecipientUserId}
                                                                onChange={(event) =>
                                                                    set_selectedRecipientUserId(event.target.value)
                                                                }
                                                                required={true}
                                                            >
                                                                <option
                                                                    key=''
                                                                    value=''
                                                                ></option>
                                                                {potentialRecipients &&
                                                                    potentialRecipients.map((p) => {
                                                                        return (
                                                                            <option
                                                                                key={p.principalId}
                                                                                value={p.principalId}
                                                                            >
                                                                                {p.name} ({p.entityName})
                                                                            </option>
                                                                        );
                                                                    })}
                                                            </select>
                                                        </div>
                                                    )}
                                                    {notice.selectRecipient == false && notice.sentToEmail && (
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            Send To:&nbsp;
                                                            <b>{notice.sentToName}</b> ({notice.sentToEmail})
                                                        </div>
                                                    )}
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-3'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='noticeDate'>
                                                            Notice Date <span className='req'>*</span>
                                                        </label>
                                                        <DateBox
                                                            id='noticeDate'
                                                            value={noticeDate ? new Date(noticeDate) : null}
                                                            onValueChange={set_noticeDate}
                                                            required={true}
                                                            placeholder='MM/DD/YYYY'
                                                            useMaskBehavior={true}
                                                            displayFormat='shortdate'
                                                        />
                                                    </div>
                                                    <div
                                                        className='columns small-12 large-6'
                                                        style={styles.formColumn}
                                                    >
                                                        <label htmlFor='responsibleId'>
                                                            Notice Sent By <span className='req'>*</span>
                                                        </label>
                                                        <select
                                                            id='sentById'
                                                            value={sentByUserId}
                                                            onChange={(event) => set_sentByUserId(event.target.value)}
                                                            required={true}
                                                        >
                                                            <option
                                                                key=''
                                                                value=''
                                                            ></option>
                                                            {employees &&
                                                                employees.map((poc) => {
                                                                    return (
                                                                        <option
                                                                            key={poc.key}
                                                                            value={poc.key}
                                                                        >
                                                                            {poc.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-3'
                                                        style={{
                                                            paddingBottom: '0px',
                                                        }}
                                                    >
                                                        <button
                                                            type='button'
                                                            className='button hollow'
                                                            title='Select Notice file to upload'
                                                            onClick={openFileSelect}
                                                        >
                                                            Add Notice File
                                                        </button>
                                                        &nbsp;
                                                    </div>
                                                    <div
                                                        className='columns small-12 large-6'
                                                        style={{
                                                            paddingBottom: '0px',
                                                        }}
                                                    >
                                                        {noticeFile && (
                                                            <span
                                                                style={{
                                                                    color: 'darkred',
                                                                }}
                                                            >
                                                                {noticeFile.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {(notice.noticeTypeId === NoticeType.AppraisalReport ||
                                                    notice.noticeTypeId === NoticeType.FloodNotice) &&
                                                    notice.address && (
                                                        <div
                                                            style={{
                                                                paddingLeft: '10px',
                                                                paddingRight: '10px',
                                                            }}
                                                        >
                                                            <h2
                                                                className='decorated'
                                                                style={styles.sectionHeader}
                                                            >
                                                                Property
                                                            </h2>
                                                            <div
                                                                className='row'
                                                                style={styles.formRow}
                                                            >
                                                                {renderPropertyItem(notice.address)}
                                                            </div>
                                                        </div>
                                                    )}

                                                {(noticeTypeId === NoticeType.IncompleteFile ||
                                                    noticeTypeId === NoticeType.AdverseAction) && (
                                                    <div
                                                        className='row'
                                                        style={styles.formRow}
                                                    >
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={styles.formColumn}
                                                        >
                                                            {notice.noticeTypeId === NoticeType.IncompleteFile && (
                                                                <>
                                                                    {incompleteReasons && incompleteReasons.length > 0 && (
                                                                        <label htmlFor='reason'>
                                                                            Reasons for Closing File{' '}
                                                                            <span className='req'>*</span>
                                                                        </label>
                                                                    )}
                                                                    {incompleteReasons &&
                                                                        incompleteReasons.map((r, index) => {
                                                                            if (r.key === IncompleteReasonType.CompanyTaxReturn) {
                                                                                return (
                                                                                    <div
                                                                                        key={`closing_${index}`}
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            flexDirection: 'row',
                                                                                            alignItems: 'flex-start',
                                                                                        }}
                                                                                    >
                                                                                        <input
                                                                                            id={`incompleteTypeId_${r.key}`}
                                                                                            type='checkbox'
                                                                                            value={r.key}
                                                                                            checked={
                                                                                                incompleteReasonTypeIds.indexOf(
                                                                                                    r.key
                                                                                                ) > -1
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                setIncompleteReasonTypeId(
                                                                                                    e.target.checked,
                                                                                                    r.key
                                                                                                )
                                                                                            }
                                                                                            disabled={reasonsLocked}
                                                                                        />
                                                                                        &nbsp;
                                                                                        {r.name}
                                                                                        <input
                                                                                            type='text'
                                                                                            id='companyTaxReturnAdditionalInfo'
                                                                                            value={companyTaxReturnAdditionalInfo}
                                                                                            onChange={(e) =>
                                                                                                set_companyTaxReturnAdditionalInfo(
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                            required={false}
                                                                                            disabled={false}
                                                                                            maxLength={255}
                                                                                            style={{
                                                                                                marginLeft: '10px',
                                                                                                width: '400px',
                                                                                            }}
                                                                                        />
                                                                                        {RenderTaxReturnHelp()}
                                                                                    </div>
                                                                                );
                                                                            } else if (
                                                                                r.key === IncompleteReasonType.PersonalTaxReturn
                                                                            ) {
                                                                                return (
                                                                                    <div
                                                                                        key={`closing_${index}`}
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            flexDirection: 'row',
                                                                                            alignItems: 'flex-start',
                                                                                        }}
                                                                                    >
                                                                                        <input
                                                                                            id={`incompleteTypeId_${r.key}`}
                                                                                            type='checkbox'
                                                                                            value={r.key}
                                                                                            checked={
                                                                                                incompleteReasonTypeIds.indexOf(
                                                                                                    r.key
                                                                                                ) > -1
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                setIncompleteReasonTypeId(
                                                                                                    e.target.checked,
                                                                                                    r.key
                                                                                                )
                                                                                            }
                                                                                            disabled={reasonsLocked}
                                                                                        />
                                                                                        &nbsp;
                                                                                        {r.name}
                                                                                        <input
                                                                                            type='text'
                                                                                            id='personalTaxReturnAdditionalInfo'
                                                                                            value={
                                                                                                personalTaxReturnAdditionalInfo
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                set_personalTaxReturnAdditionalInfo(
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                            required={false}
                                                                                            disabled={false}
                                                                                            maxLength={255}
                                                                                            style={{
                                                                                                marginLeft: '10px',
                                                                                                width: '400px',
                                                                                            }}
                                                                                        />
                                                                                        {RenderTaxReturnHelp()}
                                                                                    </div>
                                                                                );
                                                                            } else {
                                                                                return (
                                                                                    <div
                                                                                        key={`closing_${index}`}
                                                                                        style={{
                                                                                            display: 'flex',
                                                                                            flexDirection: 'row',
                                                                                            alignItems: 'flex-start',
                                                                                        }}
                                                                                    >
                                                                                        <input
                                                                                            id={`incompleteTypeId_${r.key}`}
                                                                                            type='checkbox'
                                                                                            value={r.key}
                                                                                            checked={
                                                                                                incompleteReasonTypeIds.indexOf(
                                                                                                    r.key
                                                                                                ) > -1
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                setIncompleteReasonTypeId(
                                                                                                    e.target.checked,
                                                                                                    r.key
                                                                                                )
                                                                                            }
                                                                                            disabled={reasonsLocked}
                                                                                        />
                                                                                        &nbsp;
                                                                                        {r.name}
                                                                                    </div>
                                                                                );
                                                                            }
                                                                        })}
                                                                </>
                                                            )}
                                                            {notice.noticeTypeId === NoticeType.AdverseAction && (
                                                                <>
                                                                    <label htmlFor='reason'>
                                                                        Reasons for Decline
                                                                        <span className='req'>*</span>
                                                                    </label>
                                                                    {DeclineReasonTypeOptions.sort(
                                                                        (a, b) => a.sortOrder - b.sortOrder
                                                                    ).map((r, index) => {
                                                                        return (
                                                                            <>
                                                                                <div
                                                                                    key={`decline_${index}`}
                                                                                    style={{
                                                                                        paddingBottom: '5px',
                                                                                    }}
                                                                                >
                                                                                    <input
                                                                                        id={`declineReasonId_${r.id}`}
                                                                                        type='checkbox'
                                                                                        value={r.key}
                                                                                        checked={
                                                                                            declineReasonTypeIds.indexOf(r.key) >
                                                                                            -1
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            setDeclineReasonTypeId(
                                                                                                e.target.checked,
                                                                                                r.key
                                                                                            )
                                                                                        }
                                                                                        disabled={reasonsLocked}
                                                                                    />
                                                                                    &nbsp;
                                                                                    {r.name}
                                                                                    &nbsp;
                                                                                    <i
                                                                                        style={{
                                                                                            fontSize: 'smaller',
                                                                                        }}
                                                                                    >
                                                                                        ({r.info})
                                                                                    </i>
                                                                                </div>
                                                                                {r.key ==
                                                                                    DeclineReasonType.UnableToVerifyIdentity &&
                                                                                    declineReasonTypeIds.indexOf(
                                                                                        DeclineReasonType.UnableToVerifyIdentity
                                                                                    ) > -1 && (
                                                                                        <textarea
                                                                                            style={{
                                                                                                width: '100%',
                                                                                            }}
                                                                                            rows={3}
                                                                                            id='unableToVerifyIdentityDeclineReason'
                                                                                            value={
                                                                                                unableToVerifyIdentityDeclineReason
                                                                                            }
                                                                                            onChange={(e) =>
                                                                                                set_unableToVerifyIdentityDeclineReason(
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                            required={true}
                                                                                            placeholder='Please provide more details about why the identity was unable to be verified...'
                                                                                            maxLength={255}
                                                                                            disabled={true}
                                                                                        />
                                                                                    )}
                                                                                {r.key == DeclineReasonType.Other &&
                                                                                    declineReasonTypeIds.indexOf(
                                                                                        DeclineReasonType.Other
                                                                                    ) > -1 && (
                                                                                        <textarea
                                                                                            style={{
                                                                                                width: '100%',
                                                                                            }}
                                                                                            rows={3}
                                                                                            id='reason'
                                                                                            value={declineReason}
                                                                                            onChange={(e) =>
                                                                                                set_declineReason(e.target.value)
                                                                                            }
                                                                                            required={true}
                                                                                            placeholder='Please provide more details about why this loan was declined...'
                                                                                            maxLength={255}
                                                                                            disabled={true}
                                                                                        />
                                                                                    )}
                                                                            </>
                                                                        );
                                                                    })}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ padding: '5px 5px' }}>
                                                <div
                                                    className='button-footer'
                                                    style={styles.buttonContainer}
                                                >
                                                    <button
                                                        type='button'
                                                        className='button primary'
                                                        disabled={!canSubmit()}
                                                        onClick={(e) => submitFileAndEmail(e, false)}
                                                    >
                                                        Upload File
                                                    </button>
                                                    {notice.canEmail && (
                                                        <>
                                                            &nbsp;
                                                            <button
                                                                type='button'
                                                                className='button hollow'
                                                                disabled={!canSubmit()}
                                                                title='Resend notice with new file'
                                                                onClick={(e) => submitFileAndEmail(e, true)}
                                                            >
                                                                Upload File & Email Notice
                                                            </button>
                                                        </>
                                                    )}
                                                    &nbsp;
                                                    <button
                                                        type='button'
                                                        className={noticeFile ? 'button hollow' : 'button'}
                                                        data-dismiss='modal'
                                                        onClick={_handleClose}
                                                        title='Close Notice window'
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
            <InformModal
                isOpen={informModalOpen}
                title={informModalTitle}
                text={informModalText}
                confirmCallback={() => set_informModalOpen(false)}
            />
        </>
    );
};

export default AddNoticeModal;
